import React from 'react';

import { useTranslation } from 'react-i18next';

import {

  Box, Chip, IconButton, Table, TableBody, TableCell, TableContainer,

  TableHead, TablePagination, TableRow, Tooltip, Typography,

} from '@mui/material';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import EmptyState from '../../../shared/components/EmptyState';

import {

  dataTableSx, FilterCell, HeadLabelCell, SelectColumnFilter, tableActionCellSx,
  TextColumnFilter,

} from '../../../shared/components/TableHeader';

import useTranslatedOptions from '../../../shared/hooks/useTranslatedOptions';

import { officeRowNumber } from '../model/officeForm';
import {
  normalizeOfficeAssignment,
  SCOPE_2_APPLICATION,
} from '../model/scope2Assignment';

import { EMPTY_VALUE, formatArea, formatDate } from '../../../shared/utils/number';

import { tableTokens, TOKENS } from '../../../theme/muiTheme';

import {
  LOCATION_TYPE, LOCATION_TYPE_OPTIONS, RECORD_STATUS_OPTIONS,
} from '../../../shared/constants';



const HEAD_CELLS = [

  { id: 'rowNumber', label: '#', width: 48 },

  { id: 'actions', labelKey: 'common.actions', width: 64 },

  { id: 'name', labelKey: 'office.column.name', width: 210 },

  { id: 'address', labelKey: 'office.column.address' },

  { id: 'type', labelKey: 'office.column.type', width: 140 },

  { id: 'electricity', labelKey: 'office.column.electricity', width: 260 },

  { id: 'green', labelKey: 'office.column.assignment', width: 210 },

  { id: 'area', labelKey: 'office.column.area', align: 'right', width: 130 },

  { id: 'updatedAt', labelKey: 'common.updatedAt', width: 120 },

  { id: 'status', labelKey: 'common.status', width: 130 },

];



function TypeChip({ value }) {

  const { t } = useTranslation();

  const isOffice = value === LOCATION_TYPE.OFFICE;

  return (

    <Tooltip title={t(isOffice ? 'office.type.officeHint' : 'office.type.nonOfficeHint')}>

      <Chip

        size="small"

        variant="outlined"

        label={t(isOffice ? 'office.type.office' : 'office.type.nonOffice')}

        sx={isOffice ? {
          borderColor: TOKENS.warning,
          color: TOKENS.warning,
          bgcolor: TOKENS.warningBg,
        } : {}}

      />

    </Tooltip>

  );

}



function FilterRow({ filters, onFilterChange }) {

  const { t } = useTranslation();

  const typeOptions = useTranslatedOptions(LOCATION_TYPE_OPTIONS);

  const statusOptions = useTranslatedOptions(RECORD_STATUS_OPTIONS);

  const assignmentOptions = useTranslatedOptions([
    {
      value: SCOPE_2_APPLICATION.CORPORATE_AVERAGE_REFERENCE,
      label: t('office.assignment.applicationOption.CORPORATE_AVERAGE_REFERENCE'),
    },
    {
      value: SCOPE_2_APPLICATION.GREEN_NON_FOSSIL_SOLAR,
      label: t('office.assignment.applicationOption.GREEN_NON_FOSSIL_SOLAR'),
    },
  ]);



  return (

    <TableRow>

      <FilterCell />

      <FilterCell />

      <FilterCell>

        <TextColumnFilter

          value={filters.name}

          onChange={(v) => onFilterChange('name', v)}

          ariaLabel={t('office.column.name')}

        />

      </FilterCell>

      <FilterCell>

        <TextColumnFilter

          value={filters.address}

          onChange={(v) => onFilterChange('address', v)}

          ariaLabel={t('office.column.address')}

        />

      </FilterCell>

      <FilterCell>

        <SelectColumnFilter

          value={filters.type}

          onChange={(v) => onFilterChange('type', v)}

          allLabel={t('office.filter.allTypes')}

          ariaLabel={t('office.column.type')}

          options={typeOptions}

        />

      </FilterCell>

      <FilterCell>

        <TextColumnFilter

          value={filters.electricity}

          onChange={(v) => onFilterChange('electricity', v)}

          ariaLabel={t('office.column.electricity')}

        />

      </FilterCell>

      <FilterCell>

        <SelectColumnFilter

          value={filters.assignment}

          onChange={(v) => onFilterChange('assignment', v)}

          allLabel={t('common.all')}

          ariaLabel={t('office.column.assignment')}

          options={assignmentOptions}

        />

      </FilterCell>

      <FilterCell />

      <FilterCell />

      <FilterCell>

        <SelectColumnFilter

          value={filters.status}

          onChange={(v) => onFilterChange('status', v)}

          allLabel={t('common.all')}

          ariaLabel={t('common.status')}

          options={statusOptions}

        />

      </FilterCell>

    </TableRow>

  );

}



function OfficeRow({ office, rowNumber, onEdit }) {

  const { t } = useTranslation();

  const assignment = normalizeOfficeAssignment(office, office.assignment_fiscal_year);

  const missingArea = office.location_type === LOCATION_TYPE.OFFICE

    && (office.area_m2 === null || office.area_m2 === undefined || office.area_m2 === '');



  return (

    <TableRow hover className={office.is_active ? undefined : 'office-row--inactive'}>

      <TableCell>

        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {rowNumber}
        </Typography>

      </TableCell>

      <TableCell sx={tableActionCellSx}>

        <Tooltip title={t('common.edit')}>

          <IconButton size="small" onClick={() => onEdit(office)} aria-label={t('common.edit')}>

            <EditOutlinedIcon fontSize="small" />

          </IconButton>

        </Tooltip>

      </TableCell>



      <TableCell>

        <Typography variant="body2" fontWeight={600}>{office.location_name_en}</Typography>

        <Typography variant="caption" color="text.secondary">{office.location_name_jp}</Typography>

      </TableCell>



      <TableCell sx={{ maxWidth: 380 }}>

        <Typography variant="body2" color="text.secondary">{office.location_address}</Typography>

      </TableCell>



      <TableCell><TypeChip value={office.location_type} /></TableCell>



      <TableCell sx={{ maxWidth: 300 }}>
        <Typography variant="body2" noWrap fontWeight={650}>
          {assignment.supplierName || EMPTY_VALUE}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap component="div">
          {assignment.registrationNo || EMPTY_VALUE}
        </Typography>

      </TableCell>

      <TableCell>
        {assignment.isConfigured ? (
          <Chip
            size="small"
            variant="outlined"
            label={t(`office.assignment.applicationOption.${assignment.applicationCode}`)}
            color={assignment.isGreen ? 'success' : 'default'}
          />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            label={t('office.assignment.missing')}
          />
        )}

      </TableCell>

      <TableCell align="right">

        {missingArea ? (

          <Tooltip title={t('office.areaMissingHint')}>

            <Typography variant="body2" color="warning.main" fontWeight={600}>

              {EMPTY_VALUE}

            </Typography>

          </Tooltip>

        ) : (

          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>

            {formatArea(office.area_m2)}

          </Typography>

        )}

      </TableCell>



      <TableCell>

        <Typography variant="caption" color="text.secondary">

          {formatDate(office.updated_at)}

        </Typography>

      </TableCell>



      <TableCell>

        <Chip

          size="small"

          label={t(office.is_active ? 'common.active' : 'common.inactive')}

          color={office.is_active ? 'success' : 'default'}

          variant="outlined"

        />

      </TableCell>

    </TableRow>

  );

}



export default function OfficeTable({

  offices, filters, onFilterChange, page, rowsPerPage,

  onPageChange, onRowsPerPageChange, onEdit,

}) {

  const { t } = useTranslation();

  const pageRows = offices.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);



  return (

    <Box>

      <TableContainer
        sx={{
          maxHeight: { xs: '60vh', md: 'none' },
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'text.disabled', borderRadius: 4 },
        }}
      >

        <Table
          stickyHeader
          size="small"
          sx={(theme) => ({
            ...dataTableSx(theme),
            minWidth: 1200,
            '& th:nth-of-type(1), & td:nth-of-type(1), & th:nth-of-type(2), & td:nth-of-type(2)': {
              position: 'sticky',
            },
            '& th:nth-of-type(1), & td:nth-of-type(1)': {
              left: 0,
              width: 48,
              minWidth: 48,
              maxWidth: 48,
            },
            '& th:nth-of-type(2), & td:nth-of-type(2)': {
              left: 48,
              minWidth: 64,
            },
            '& td:nth-of-type(1), & td:nth-of-type(2)': {
              zIndex: 2,
              bgcolor: 'background.paper',
            },
            '& .office-row--inactive > td': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.08)' : '#f2f4f8',
            },
            '& thead th:nth-of-type(1), & thead th:nth-of-type(2)': {
              zIndex: 4,
              backgroundColor: tableTokens(theme).headBg,
            },
          })}
        >

          <TableHead>

            <TableRow>

              {HEAD_CELLS.map((cell) => (

                <HeadLabelCell key={cell.id} align={cell.align} width={cell.width}>

                  {cell.label || t(cell.labelKey)}

                </HeadLabelCell>

              ))}

            </TableRow>

            <FilterRow filters={filters} onFilterChange={onFilterChange} />

          </TableHead>

          <TableBody>

            {pageRows.map((office, index) => (

              <OfficeRow
                key={office.id}
                office={office}
                rowNumber={officeRowNumber(page, rowsPerPage, index)}
                onEdit={onEdit}
              />

            ))}

            {pageRows.length === 0 && (

              <TableRow>

                <TableCell colSpan={HEAD_CELLS.length} sx={{ border: 0 }}>

                  <EmptyState

                    title={t('common.noResults')}

                    description={t('common.noResultsHint')}

                  />

                </TableCell>

              </TableRow>

            )}

          </TableBody>

        </Table>

      </TableContainer>



      <TablePagination

        component="div"

        count={offices.length}

        page={page}

        onPageChange={(event, next) => onPageChange(next)}

        rowsPerPage={rowsPerPage}

        onRowsPerPageChange={(event) => onRowsPerPageChange(Number(event.target.value))}

        rowsPerPageOptions={[10, 25, 50]}

        labelRowsPerPage={t('common.rowsPerPage')}

        sx={{
          '& .MuiTablePagination-toolbar': { px: { xs: 1, sm: 2 }, flexWrap: 'nowrap' },
          '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
            display: { xs: 'none', sm: 'block' },
          },
          '& .MuiTablePagination-displayedRows': { ml: { xs: 0 }, flex: { xs: 1 }, whiteSpace: 'nowrap' },
          '& .MuiTablePagination-actions': { ml: { xs: 0.5, sm: 2.5 } },
        }}
      />

    </Box>

  );

}
